import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  Store, 
  ShoppingCart, 
  DollarSign, 
  CheckSquare,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalRetailers: 0,
    activeRetailers: 0,
    totalOrders: 0,
    pendingPayments: 0,
    totalRevenue: 0,
    pendingTasks: 0
  });
  const [orderStatus, setOrderStatus] = useState([]);
  const [upcomingFollowups, setUpcomingFollowups] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, orderStatusRes, followupsRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/analytics/orders/status'),
        api.get('/analytics/followups/upcoming')
      ]);

      setStats(statsRes.data);
      setOrderStatus(orderStatusRes.data);
      setUpcomingFollowups(followupsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container">Loading dashboard...</div>;
  }

  return (
    <div className="container">
      <h1 style={{ marginBottom: '30px' }}>Dashboard</h1>

      <div className="grid grid-4" style={{ marginBottom: '30px' }}>
        <div className="stat-card" style={{ borderLeft: '4px solid #28a745' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div className="stat-value">{stats.totalRetailers}</div>
              <div className="stat-label">Total Retailers</div>
            </div>
            <Store size={32} style={{ color: '#28a745' }} />
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #007bff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div className="stat-value">{stats.totalOrders}</div>
              <div className="stat-label">Total Orders</div>
            </div>
            <ShoppingCart size={32} style={{ color: '#007bff' }} />
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #ffc107' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div className="stat-value">₹{stats.totalRevenue?.toFixed(2) || '0.00'}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
            <DollarSign size={32} style={{ color: '#ffc107' }} />
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #dc3545' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div className="stat-value">{stats.pendingTasks}</div>
              <div className="stat-label">Pending Tasks</div>
            </div>
            <CheckSquare size={32} style={{ color: '#dc3545' }} />
          </div>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginBottom: '30px' }}>
        <div className="card">
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
            <TrendingUp size={20} style={{ marginRight: '8px' }} />
            Order Status Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={orderStatus}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#FF69B4" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
            <AlertCircle size={20} style={{ marginRight: '8px' }} />
            Upcoming Follow-ups
          </h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {upcomingFollowups.length === 0 ? (
              <p style={{ color: '#666' }}>No upcoming follow-ups</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Retailer</th>
                    <th>Type</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingFollowups.slice(0, 10).map((followup, index) => (
                    <tr key={`${followup._id}-${followup.followup_type}-${index}`}>
                      <td>{followup.name}</td>
                      <td>
                        <span className={`badge ${
                          followup.followup_type === 'phone_due' ? 'badge-info' : 'badge-warning'
                        }`}>
                          {followup.followup_type === 'phone_due' ? 'Phone' : 'Visit'}
                        </span>
                      </td>
                      <td>
                        {formatDate(followup.followup_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.href = '/orders'}
          >
            Create New Order
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => window.location.href = '/retailers'}
          >
            Add Retailer
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => window.location.href = '/tasks'}
          >
            View Tasks
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => window.location.href = '/payments'}
          >
            Check Payments
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;